/*
 * Opções do GRÁFICO (item CHART) na interface: os tipos, as paletas e as escolhas da
 * aparência com as chaves de tradução, os dados de exemplo e a leitura do que o usuário
 * cola ou importa. O modelo, a validação e o desenho moram em chart-render/chart-render.ts.
 */

import {
    CHART_LIMITS,
    CHART_PALETTES,
    ChartHeight,
    ChartKind,
    ChartLegend,
    ChartPalette,
    ChartUnit,
    ChartValue,
    DEFAULT_CHART_STYLE,
    isPieKind,
    sanitizeChartValue,
} from './chart-render/chart-render';

const K = 'document.edit.chart';

export interface ChartKindOption {
    key: ChartKind;
    labelKey: string;
    hintKey: string;
}

/** Os 8 tipos, na ordem do assistente (comparar, compor, evoluir, participar). */
export const CHART_KIND_OPTIONS: ChartKindOption[] = (
    ['column', 'bar', 'stacked', 'combo', 'line', 'area', 'pie', 'donut'] as ChartKind[]
).map(key => ({ key, labelKey: `${K}.kinds.${key}.name`, hintKey: `${K}.kinds.${key}.hint` }));

export const CHART_PALETTE_OPTIONS: { key: ChartPalette; labelKey: string; colors: readonly string[] }[] = (
    Object.keys(CHART_PALETTES) as ChartPalette[]
).map(key => ({ key, labelKey: `${K}.palettes.${key}`, colors: CHART_PALETTES[key] }));

export const CHART_LEGEND_OPTIONS: { key: ChartLegend; labelKey: string }[] = [
    { key: 'top', labelKey: `${K}.legend.top` },
    { key: 'bottom', labelKey: `${K}.legend.bottom` },
    { key: 'none', labelKey: `${K}.legend.none` },
];

export const CHART_UNIT_OPTIONS: { key: ChartUnit; labelKey: string }[] = [
    { key: 'number', labelKey: `${K}.unit.number` },
    { key: 'currency', labelKey: `${K}.unit.currency` },
    { key: 'percent', labelKey: `${K}.unit.percent` },
];

export const CHART_HEIGHT_OPTIONS: { key: ChartHeight; labelKey: string }[] = [
    { key: 'low', labelKey: `${K}.height.low` },
    { key: 'medium', labelKey: `${K}.height.medium` },
    { key: 'high', labelKey: `${K}.height.high` },
];

/** Séries que cabem: as colunas menos a de categorias. */
export const CHART_MAX_SERIES = CHART_LIMITS.MAX_COLUMNS - 1;

/**
 * Gráfico de exemplo do assistente: a área executada de uma obra, previsto x realizado,
 * nos últimos seis meses. Serve para o cliente ver o gráfico antes de ter os dados; o
 * aviso "dados de exemplo" fica na tela até ele mexer na tabela. Pizza e rosca desenham
 * só uma série, então o exemplo delas vem só com o previsto: uma coluna "fora da pizza"
 * no exemplo seria ruído e ensinaria errado.
 */
export function exampleChartValue(kind: ChartKind, t: (key: string) => string): ChartValue {
    const months = t(`${K}.example.months`).split(',');
    const planned = ['120', '132', '101', '134', '90', '230'];
    const actual = ['98', '140', '95', '150', '88', '210'];
    const single = isPieKind(kind);
    return {
        kind,
        title: t(`${K}.example.title`),
        columns: single
            ? [t(`${K}.example.category`), t(`${K}.example.planned`)]
            : [t(`${K}.example.category`), t(`${K}.example.planned`), t(`${K}.example.actual`)],
        rows: months
            .slice(0, 6)
            .map((month, i) => (single ? [month.trim(), planned[i]] : [month.trim(), planned[i], actual[i]])),
        style: { ...DEFAULT_CHART_STYLE },
    };
}

/** Lê o valor gravado de um item CHART (ou null se não for um gráfico). */
export function parseChartValue(raw: unknown): ChartValue | null {
    const value = sanitizeChartValue(raw);
    if (!value) return null;
    const record = raw as Record<string, unknown>;
    if (typeof record['svg'] === 'string') value.svg = record['svg'];
    if (typeof record['svgCompact'] === 'string') value.svgCompact = record['svgCompact'];
    return value;
}

// #region Colar e importar

/**
 * Transforma texto copiado de planilha (Excel, Google Planilhas: colunas separadas por
 * TAB) ou um CSV (`;` ou `,`) numa matriz de células. Aspas de CSV são respeitadas, e
 * linhas vazias no fim somem.
 */
export function parseDelimitedText(text: string): string[][] {
    const clean = String(text ?? '').replace(/\r\n?/g, '\n').replace(/\n+$/, '');
    if (!clean.trim()) return [];

    const firstLine = clean.split('\n', 1)[0];
    const delimiter = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';

    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;

    for (let i = 0; i < clean.length; i++) {
        const ch = clean[i];
        if (quoted) {
            if (ch === '"' && clean[i + 1] === '"') {
                cell += '"';
                i++;
            } else if (ch === '"') {
                quoted = false;
            } else {
                cell += ch;
            }
        } else if (ch === '"' && cell.trim() === '') {
            quoted = true;
            cell = '';
        } else if (ch === delimiter) {
            row.push(cell.trim());
            cell = '';
        } else if (ch === '\n') {
            row.push(cell.trim());
            rows.push(row);
            row = [];
            cell = '';
        } else {
            cell += ch;
        }
    }
    row.push(cell.trim());
    rows.push(row);

    return rows.filter(r => r.some(c => c !== ''));
}

/** Uma linha de cabeçalho: tem nomes onde as séries teriam números. */
export function looksLikeHeader(row: string[], isNumber: (text: string) => boolean): boolean {
    const rest = row.slice(1).filter(c => c !== '');
    return row.length > 1 && rest.length > 0 && rest.every(c => !isNumber(c));
}

/**
 * Matriz colada/importada => colunas e linhas do gráfico. A primeira linha vira o nome
 * das séries quando parece cabeçalho; senão as colunas ficam sem nome. Respeita os
 * limites (7 colunas, 50 linhas) e completa as linhas curtas.
 */
export function gridFromMatrix(
    matrix: string[][],
    isNumber: (text: string) => boolean
): { columns: string[]; rows: string[][] } | null {
    if (matrix.length === 0) return null;
    const header = looksLikeHeader(matrix[0], isNumber);
    const body = header ? matrix.slice(1) : matrix;
    const width = Math.min(
        CHART_LIMITS.MAX_COLUMNS,
        Math.max(CHART_LIMITS.MIN_COLUMNS, ...matrix.map(r => r.length))
    );
    const fit = (r: string[]) => Array.from({ length: width }, (_, i) => r[i] ?? '');
    const rows = body.slice(0, CHART_LIMITS.MAX_ROWS).map(fit);
    return {
        columns: header ? fit(matrix[0]) : Array.from({ length: width }, () => ''),
        rows: rows.length > 0 ? rows : [fit([])],
    };
}

// #endregion
